import { CloseButton } from "lib/headlessui/components/close-button/close-button";
import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
} from "lib/headlessui/components/dialog/dialog";
import { ActionType } from "@sp-crm/core";
import { NgSidebar } from "components/app/ng-sidebar";
import { getNavigationSections } from "config/navigation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
    useFeature,
    useIsAllowed,
    useIsAllowedInWholeTenant,
    useIsMultiRegionUser,
} from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { isAllowed } from "util/permissions";
import { Navbar, NavbarItem, NavbarSection } from "./navbar";
import { useNavbarPortal } from "./navbar-portal-context";

function OpenMenuIcon() {
    return (
        <svg
            data-slot="icon"
            viewBox="0 0 20 20"
            aria-hidden="true"
            className="size-6 sm:size-5 text-black">
            <path
                d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z"
                fill="currentColor"
            />
        </svg>
    );
}

function CloseMenuIcon() {
    return (
        <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
        </svg>
    );
}

function MobileSidebar({
    open,
    close,
    children,
}: React.PropsWithChildren<{ open: boolean; close: () => void }>) {
    return (
        <Dialog open={open} onClose={close} className="lg:hidden">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-black/30 transition data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />
            <DialogPanel
                transition
                className="fixed inset-y-0 w-full max-w-80 p-2 transition duration-300 ease-in-out data-[closed]:-translate-x-full">
                <div className="flex h-full flex-col rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
                    <div className="-mb-3 px-4 pt-3">
                        <CloseButton as={NavbarItem} aria-label="Close navigation">
                            <CloseMenuIcon />
                        </CloseButton>
                    </div>
                    {children}
                </div>
            </DialogPanel>
        </Dialog>
    );
}

export function SidebarLayout({ children }: React.PropsWithChildren) {
    const [showSidebar, setShowSidebar] = useState(false);
    const { setPortalTarget } = useNavbarPortal();
    const navbarRef = useRef<HTMLDivElement>(null);
    const { pathname } = useLocation();

    const sidebarState = useSelector(
        (state: ApplicationState) => state.design.sidebarState || "expanded",
    );
    const collapsed = sidebarState === "collapsed";

    const showReferrals = useSelector((state: ApplicationState) =>
        isAllowed(ActionType.ViewReference, state.permissions, state.region),
    );
    const legacyReportsEnabled = useIsAllowed(ActionType.ViewReports);
    const viewAllEnabled = useIsAllowed(ActionType.ViewAllInvoices);
    const viewOwnEnabled = useIsAllowed(ActionType.ViewOwnInvoices);
    const viewCustomReportsEnabled = useIsAllowed(
        ActionType.AdvancedSearchExplicitExecute,
    );
    const referralSourceReportEnabled = useFeature("referralSourceReport");
    const homeCarePrototypeEnabled = useFeature("homeCarePrototype");
    const isMultiRegionUser = useIsMultiRegionUser();
    const editAgencySettingsAllowed = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const editRegionSettingsAllowed = useIsAllowed(ActionType.UpdateRegionSettings);
    const manageUsersAllowed = useIsAllowed(ActionType.SelfManageModifyUser);
    const manageIntegrationsAllowed = useIsAllowed(ActionType.ManageIntegrations);
    const manageWorkflowsAllowed = useIsAllowed(ActionType.CreateWorkflow);

    const showAgencySettings = editAgencySettingsAllowed;
    const showRegionSettings =
        (editRegionSettingsAllowed && isMultiRegionUser) ||
        (editRegionSettingsAllowed && !editAgencySettingsAllowed);

    const csvDownloadEnabled = useIsAllowed(ActionType.ExportCsv);
    const stageConversionEnabled = useFeature("stageConversion");

    const navigationSections = useMemo(
        () =>
            getNavigationSections({
                showReferrals,
                legacyReportsEnabled,
                viewAllEnabled,
                viewOwnEnabled,
                viewCustomReportsEnabled,
                referralSourceReportEnabled,
                stageConversionEnabled,
                showAgencySettings,
                showRegionSettings,
                manageWorkflowsAllowed,
                manageUsersAllowed,
                manageIntegrationsAllowed,
                csvDownloadEnabled,
                homeCarePrototypeEnabled,
            }),
        [
            showReferrals,
            legacyReportsEnabled,
            viewAllEnabled,
            viewOwnEnabled,
            viewCustomReportsEnabled,
            referralSourceReportEnabled,
            stageConversionEnabled,
            showAgencySettings,
            showRegionSettings,
            manageWorkflowsAllowed,
            manageUsersAllowed,
            manageIntegrationsAllowed,
            csvDownloadEnabled,
            homeCarePrototypeEnabled,
        ],
    );

    const activeSection = useMemo(
        () => navigationSections.find(section => section.isActive(pathname)),
        [navigationSections, pathname],
    );

    const navItems = useMemo(() => {
        if (!activeSection || !activeSection.expandable) {
            return [];
        }
        if (activeSection.id === "clients" && pathname.startsWith("/clients/show")) {
            return [];
        }
        return activeSection.items.filter(item => item.visible !== false);
    }, [activeSection, pathname]);

    useEffect(() => {
        if (navbarRef.current) {
            setPortalTarget(navbarRef.current);
        }
        return () => setPortalTarget(null);
    }, [setPortalTarget]);

    const sidebarStyles = collapsed
        ? { width: "w-16", contentPadding: "lg:pl-20", left: "left-0" }
        : { width: "w-52", contentPadding: "lg:pl-56", left: "left-2" };

    const handleOpenSidebar = useCallback(() => {
        setShowSidebar(true);
    }, []);

    const handleCloseSidebar = useCallback(() => {
        setShowSidebar(false);
    }, []);

    return (
        <div className="relative isolate flex min-h-svh w-full bg-white max-lg:flex-col lg:bg-zinc-100 dark:bg-zinc-900 dark:lg:bg-zinc-950">
            {/* Sidebar on desktop */}
            <div
                className={`fixed inset-y-0 ${sidebarStyles.left} ${sidebarStyles.width} max-lg:hidden`}>
                <NgSidebar mode={collapsed ? "icons" : "full"} expandable={false} />
            </div>

            {/* Sidebar on mobile (always full width) */}
            <MobileSidebar open={showSidebar} close={handleCloseSidebar}>
                <NgSidebar mode="full" expandable={true} />
            </MobileSidebar>

            {/* Navbar on mobile */}
            <header className="px-4 lg:hidden">
                <div className="py-2.5">
                    <button onClick={handleOpenSidebar} aria-label="Open navigation">
                        <OpenMenuIcon />
                    </button>
                </div>
                <div ref={navbarRef} />
            </header>

            {/* Content */}
            <main
                className={`flex flex-1 flex-col lg:min-w-0 ${sidebarStyles.contentPadding} lg:pr-2`}>
                {/* Navbar on desktop */}
                {navItems.length > 0 && (
                    <div className="max-lg:hidden px-4 dark:border-zinc-700 -mb-4">
                        <Navbar>
                            <NavbarSection>
                                {navItems.map(({ label, url, activeMode = "exact" }) => (
                                    <NavbarItem
                                        key={url}
                                        href={url}
                                        current={
                                            (activeMode === "exact"
                                                ? pathname === url
                                                : pathname.startsWith(url)) ||
                                            (url === "/pipeline" && pathname === "/")
                                        }>
                                        {label}
                                    </NavbarItem>
                                ))}
                            </NavbarSection>
                        </Navbar>
                    </div>
                )}

                <div className="relative grow lg:rounded-lg bg-white lg:shadow-sm lg:ring-1 lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10 mt-4">
                    {children}
                </div>
            </main>
        </div>
    );
}
